import { Rss } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { FeedChannel } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useStoreDetail } from '@/features/stores';
import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { FeedChannelCard } from '../components/feed-channel-card';
import { FeedSettingsCard } from '../components/feed-settings-card';
import { RotateFeedDialog } from '../components/rotate-feed-dialog';
import { useFeedStatus } from '../hooks/use-feed-status';
import { useRotateFeedToken } from '../hooks/use-rotate-feed-token';
import { FEED_CHANNELS } from '../schemas/feed-channels';

function Feeds() {
  const { t } = useTranslation('feeds');
  const store = useStore();
  const query = useFeedStatus(store.id);
  const detail = useStoreDetail(store.id);
  const rotate = useRotateFeedToken(store.id);
  const [confirming, setConfirming] = useState<FeedChannel | null>(null);
  const { editable, reason } = useStoreEditable({ permission: 'content.write' });

  // A first URL replaces nothing, so only a rotation asks for confirmation.
  const onRotate = (channel: FeedChannel, enabled: boolean) => {
    rotate.reset();
    if (enabled) setConfirming(channel);
    else rotate.mutate(channel);
  };

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        backTo={{ to: '/org/stores/$storeId', params: { storeId: store.id } }}
        backLabel={t('back')}
      />
      <ReadOnlyBanner reason={reason} />
      <Alert className="mb-4">
        <Rss aria-hidden />
        <AlertDescription>{t('intro')}</AlertDescription>
      </Alert>
      <div className="flex flex-col gap-6">
        {query.isPending ? (
          <Skeleton className="h-64" />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <div className="grid gap-4 xl:grid-cols-3">
            {FEED_CHANNELS.map(({ channel, key }, index) => (
              <FeedChannelCard
                key={channel}
                tour={index === 0}
                channel={channel}
                status={query.data[key]}
                editable={editable}
                rotating={rotate.isPending && rotate.variables === channel}
                onRotate={(c) => onRotate(c, query.data[key].enabled)}
              />
            ))}
          </div>
        )}
        {rotate.isError && confirming === null ? <ErrorState error={rotate.error} compact /> : null}
        <div className="max-w-2xl">
          {detail.data ? (
            <FeedSettingsCard key={detail.data.updatedAt} store={detail.data} />
          ) : detail.error ? (
            <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
          ) : (
            <Skeleton className="h-40" />
          )}
        </div>
      </div>
      <RotateFeedDialog
        channel={confirming}
        pending={rotate.isPending}
        error={rotate.error}
        onConfirm={(channel) => rotate.mutate(channel, { onSuccess: () => setConfirming(null) })}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
      />
    </PageContent>
  );
}

export function FeedsPage() {
  return (
    <FeatureErrorBoundary name="feeds">
      <Feeds />
    </FeatureErrorBoundary>
  );
}
